import React from 'react'
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material'
import type {
  AssistantRoutineClock,
  AssistantRoutinePeriod,
  AssistantRoutineSchedule,
  AssistantWeekday,
} from './assistantProfiles'

const PERIODS: Array<{ id: AssistantRoutinePeriod; label: string }> = [
  { id: 'last_24_hours', label: 'Last 24 hours' },
  { id: 'today', label: 'Today' },
  { id: 'previous_day', label: 'Previous day' },
  { id: 'this_week', label: 'This week' },
  { id: 'last_7_days', label: 'Last 7 days' },
  { id: 'previous_week', label: 'Previous week' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'previous_month', label: 'Previous month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'previous_quarter', label: 'Previous quarter' },
  { id: 'this_year', label: 'This year' },
  { id: 'previous_year', label: 'Previous year' },
]

export const RoutinePeriodField: React.FC<{
  value: AssistantRoutinePeriod
  onChange: (next: AssistantRoutinePeriod) => void
}> = ({ value, onChange }) => (
  <FormControl size='small' fullWidth>
    <InputLabel>Data period</InputLabel>
    <Select
      label='Data period'
      value={value}
      onChange={event => onChange(event.target.value as AssistantRoutinePeriod)}
    >
      {PERIODS.map(item => (
        <MenuItem key={item.id} value={item.id}>
          {item.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

const REPEATS: Array<{ id: AssistantRoutineSchedule; label: string }> = [
  { id: 'once', label: 'Once' },
  { id: 'daily', label: 'Every day' },
  { id: 'weekdays', label: 'Weekdays' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'custom', label: 'Custom days' },
]

const DAYS: AssistantWeekday[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function describeRoutineClock(clock: AssistantRoutineClock): string {
  const when = clock.times
    .map(value => {
      const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value)
      if (!match) return null
      const hour24 = Number(match[1])
      const hour = hour24 % 12 || 12
      const suffix = hour24 < 12 ? 'AM' : 'PM'
      return `${hour}:${match[2]} ${suffix}`
    })
    .filter(Boolean)
    .join(', ')
  const timeLabel = when || '8:00 AM'
  if (clock.schedule === 'once') return `Once on ${clock.date || 'a date'} at ${timeLabel}`
  if (clock.schedule === 'daily') return `Every day at ${timeLabel}`
  if (clock.schedule === 'weekdays') return `Weekdays at ${timeLabel}`
  const days = (clock.days.length ? clock.days : ['Mon']).join(', ')
  if (clock.schedule === 'weekly') return `Weekly on ${days} at ${timeLabel}`
  return `${days} at ${timeLabel}`
}

export const RoutineScheduleFields: React.FC<{
  value: AssistantRoutineClock
  onChange: (next: AssistantRoutineClock) => void
}> = ({ value, onChange }) => {
  const showDays = value.schedule === 'weekly' || value.schedule === 'custom'
  const times = value.times.length ? value.times : ['08:00']

  const setTime = (index: number, next: string) => {
    const copy = [...times]
    copy[index] = next
    onChange({ ...value, times: value.schedule === 'once' ? [copy[0] || '08:00'] : copy })
  }

  return (
    <Stack spacing={1}>
      <FormControl size='small' fullWidth>
        <InputLabel>Repeat</InputLabel>
        <Select
          label='Repeat'
          value={value.schedule}
          onChange={event => {
            const schedule = event.target.value as AssistantRoutineSchedule
            const days =
              schedule === 'weekly' || schedule === 'custom'
                ? value.days.length
                  ? value.days
                  : (['Mon'] as AssistantWeekday[])
                : []
            onChange({
              schedule,
              times: schedule === 'once' ? [times[0] || '08:00'] : times,
              days,
              date: schedule === 'once' ? value.date : null,
            })
          }}
        >
          {REPEATS.map(item => (
            <MenuItem key={item.id} value={item.id}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showDays ? (
        <Stack direction='row' flexWrap='wrap' gap={0.5}>
          {DAYS.map(day => {
            const selected = value.days.includes(day)
            return (
              <Button
                key={day}
                size='small'
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => {
                  const days = selected ? value.days.filter(item => item !== day) : [...value.days, day]
                  const ordered = DAYS.filter(item => days.includes(item))
                  onChange({ ...value, days: ordered.length ? ordered : [day] })
                }}
                sx={{ minWidth: 44, minHeight: 32, px: 1, textTransform: 'none' }}
              >
                {day}
              </Button>
            )
          })}
        </Stack>
      ) : null}
      {value.schedule === 'once' ? (
        <TextField
          label='Date'
          type='date'
          size='small'
          value={value.date || ''}
          onChange={event => onChange({ ...value, date: event.target.value || null })}
          InputLabelProps={{ shrink: true }}
        />
      ) : null}
      {times.map((time, index) => (
        <Stack key={`${index}:${time}`} direction='row' spacing={1} alignItems='center'>
          <TextField
            label={times.length > 1 ? `Time ${index + 1}` : 'Time'}
            type='time'
            size='small'
            value={time}
            onChange={event => setTime(index, event.target.value || '08:00')}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1 }}
          />
          {value.schedule !== 'once' && times.length > 1 ? (
            <Button
              size='small'
              color='inherit'
              onClick={() => onChange({ ...value, times: times.filter((_, item) => item !== index) })}
              sx={{ textTransform: 'none', minHeight: 32 }}
            >
              Remove
            </Button>
          ) : null}
        </Stack>
      ))}
      {value.schedule !== 'once' && times.length < 8 ? (
        <Box>
          <Button
            size='small'
            color='inherit'
            onClick={() => onChange({ ...value, times: [...times, '08:00'] })}
            sx={{ textTransform: 'none', minHeight: 32 }}
          >
            Add time
          </Button>
        </Box>
      ) : null}
    </Stack>
  )
}
